import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { cloneDeep, isNaN } from "lodash";
import {
    AsyncModalService,
    ButtonPresets,
} from "src/app/services/shared/async-modal-service";
import { GeneratorService } from "../../services/settings/codegenerator.service";

@Component({
    standalone: false,
    selector: "generator-form",
    templateUrl: "./generator.form.component.html",
    providers: [GeneratorService],
})
export class FormCodeGenerator implements OnInit, OnDestroy {
    @Input() type: string;

    entity: any = {};
    initialEntity: any = {};
    form: FormGroup;
    fields: Array<FormlyFieldConfig> = [];

    constructor(
        private fb: FormBuilder,
        private service: GeneratorService,
        private route: ActivatedRoute,
        private asyncModalService: AsyncModalService
    ) {
        this.form = fb.group({});
        this.fields = this.getGroupFields();
        this.entity = {};
    }

    getGroupFields(): Array<FormlyFieldConfig> {
        return [
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    {
                        className: "col-12 col-sm-12 col-md-4",
                        key: "start",
                        type: "hz-select",
                        defaultValue: "Year",
                        props: {
                            label: "Part 1",
                            required: false,
                            options: [
                                { label: "Year", value: "Year" },
                                { label: "Number", value: "Number" },
                                { label: "Text", value: "Text" },
                            ],
                            change: ($viewValue) => {
                                this.generate(event);
                            },
                        },
                        expressions: {
                            "props.required": "model.active",
                        },
                    },
                    {
                        className: "col-12 col-sm-12 col-md-4",
                        key: "separator",
                        type: "hz-select",
                        defaultValue: "Backslash",
                        props: {
                            model: this.entity,
                            label: "Part 2",
                            required: false,
                            options: [
                                { label: "/", value: "Backslash" },
                                { label: ".", value: "Point" },
                                { label: "-", value: "Dash" },
                                { label: "Blank", value: "None" },
                            ],
                            change: ($viewValue) => {
                                this.generate(event);
                            },
                        },
                        expressions: {
                            "props.required": "model.active",
                        },
                    },
                    {
                        className: "col-12 col-sm-12 col-md-4",
                        key: "end",
                        type: "hz-select",
                        defaultValue: "Text",
                        props: {
                            label: "Part 3",
                            required: false,
                            options: [
                                { label: "Year", value: "Year" },
                                { label: "Number", value: "Number" },
                                { label: "Text", value: "Text" },
                            ],
                            change: ($viewValue) => {
                                this.generate(event);
                            },
                        },
                        expressions: {
                            "props.required": "model.active",
                        },
                    },
                ],
                hideExpression: "!model.active",
            },
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    {
                        className: "col-12 col-sm-12 col-md-4",
                        key: "baseStart",
                        type: "hz-input",
                        props: {
                            required: false,
                            keyup: (event) => {
                                this.generate(event);
                            },
                        },
                        expressions: {
                            "props.required": "model.active",
                        },
                    },
                    {
                        className: "col-12 col-sm-12 col-md-4",
                        key: "baseSeparator",
                        type: "hz-input",
                        props: {
                            required: false,
                            type: "hidden",
                            disabled: true,
                        },
                    },
                    {
                        className: "col-12 col-sm-12 col-md-4",
                        key: "baseEnd",
                        type: "hz-input",
                        props: {
                            required: false,
                            keyup: (event) => {
                                this.generate(event);
                            },
                        },
                        expressions: {
                            "props.required": "model.active",
                        },
                    },
                ],
                hideExpression: "!model.active",
            },
        ];
    }

    onActiveChange() {
        this.fields = this.getGroupFields();

        if (this.entity.active) {
            this.generate(null);
        }
    }

    ngOnInit() {
        this.route.params.subscribe((params) => {
            this.entity.type = this.type;
            this.bind(this.type);
        });
    }

    ngOnDestroy() {}

    bind(type: string) {
        this.service.get(type).subscribe(
            (data) => {
                if (data.id > 0) {
                this.form = this.fb.group({});
                this.fields = this.getGroupFields();
                this.entity = data;
                this.initialEntity = cloneDeep(data);

                    this.generate(null);
                }
            },
            (err) => this.asyncModalService.getModalResponse(err.error)
        );
    }

    getFieldByKey(key: string) {
        const fields = this.fields[0].fieldGroup;

        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];

            if (field.key == key) {
                return field;
            }
        }
    }

    async submit() {
        var confirmationReqired =
            !this.initialEntity.active && this.entity.active;
        if (
            (confirmationReqired &&
                (await this.asyncModalService.getModalResponse(
                    "If you have existing order numbers generated by the system, enabling the code generator could create duplicate order numbers. Please ensure the last recorded order number is entered.",
                    ButtonPresets.YesNo
                ))) ||
            !confirmationReqired
        ) {
            if (this.entity.id == null || this.entity.id == 0)
                this.service.add(this.entity).subscribe(
                    (data) => {
                        this.entity = data;
                        this.initialEntity = data.active;
                    },
                    (err) => this.asyncModalService.getModalResponse(err.error),
                    () => {
                        this.bind(this.type);
                        this.asyncModalService.getModalResponse(
                            "Save successful"
                        );
                    }
                );
            else
                this.service.update(this.entity.id, this.entity).subscribe(
                    (data) => {
                        this.merge(data);
                        this.initialEntity = data.active;
                    },
                    null,
                    () => {
                        this.bind(this.type);
                        this.asyncModalService.getModalResponse(
                            "Save successful"
                        );
                    }
                );
        }
    }

    generate($event) {
        switch (this.entity.separator) {
            case "Dash":
                this.entity.baseSeparator = "-";
                break;
            case "Backslash":
                this.entity.baseSeparator = "/";
                break;
            case "Point":
                this.entity.baseSeparator = ".";
                break;
            default:
                this.entity.baseSeparator = "";
                break;
        }

        this.entity.baseCode =
            this.entity.baseStart +
            this.entity.baseSeparator +
            this.entity.baseEnd;
        this.entity.nextCode =
            this.getNext(this.entity.start, this.entity.baseStart) +
            this.entity.baseSeparator +
            this.getNext(this.entity.end, this.entity.baseEnd);
    }

    getNext(segment: string, value: any): any {
        switch (segment) {
            case "Number":
                if (isNaN(Number(value))) {
                    return value;
                }

                let number = (+value || 0) + 1;

                var zeroLength = Math.max(
                    0,
                    value.toString().length - number.toString().length
                );
                return `${Array(zeroLength + 1).join("0")}${number}`;

            default:
                return value;
        }
    }

    merge(code) {
        for (let prop in code) {
            this.entity[prop] = code[prop];
        }
    }
}
